import { Button, Modal, Input, message, Spin } from 'antd';
import { observer } from 'mobx-react';
import React, { FC, useState, useEffect } from 'react';
import { getUserName, isProOrSgp } from '@/infrastructure/auth';
import { getProductId } from '@/infrastructure/product-info';
import { CUSTOM_ACTIONS } from '@/infrastructure/reporter/datong/constant';
import { reportClick } from '@/infrastructure/reporter/datong/report';
import {
  createTapdIssue as createDefectIssue,
  CreateTapdIssueReq as CreateDefectIssueReq,
} from '@/presentation/components/tapd-info/api';
import { RenderAutoComplete } from '@/presentation/components/tapd-info/create-tapd-modal/auto-complete';
import { RenderInput } from '@/presentation/components/tapd-info/create-tapd-modal/input';
import { RenderSelect } from '@/presentation/components/tapd-info/create-tapd-modal/select';
import { RenderUserSelect } from '@/presentation/components/tapd-info/create-tapd-modal/user-select';
import { IWebhookProjectInfo } from '@/presentation/pages/defect-manage/api';
import { DefaultComponentType, FIELDS_PRESET } from '@/presentation/pages/defect-manage/protocol';
import { useStores } from '@/presentation/store';
import { FilterOptions } from '@/proto/alert';
import { Zone } from '@/proto/field';
import { getTypeByZone } from '@/proto/issue';
import { useTranslation } from 'react-i18next';

export interface FieldsType {
  name: string | null | undefined;
  label: string | null | undefined;
  value: string | number | null | undefined;
  type: string | null | undefined;
  must: number | null | undefined; // 是否必填 0: 非必填 1: 必填
  options?: FilterOptions[] | undefined;
}

export type CustomFieldsType = Omit<FieldsType, 'value'>;

export const formatFieldsValue = (fields: Record<string, any>[]) => fields.map(item => ({
  name: item.name,
  value: item.value,
  label: item.label,
})).filter(item => item.name !== 'title_prefix');

export const CreateDefectModal: FC<{
  type: Zone,
  title: string,
  content: string,
  issueKey: string,
  issueUrl: string,
  pageId: string,
  visible: boolean,
  onClose: () => void,
  onChangeId: (workspaceId: string) => void,
  onSubmit?: () => void,
  defaultAppVersion: string,
}> = observer(({
  title,
  content,
  issueKey,
  issueUrl,
  type,
  pageId,
  visible,
  onClose,
  onChangeId,
  onSubmit,
  defaultAppVersion,
}) => {
  const { t } = useTranslation();
  const { defectList: originalDefectList, getFullDefectList } = useStores().DefectStore;
  const initStatusParams: CreateDefectIssueReq = {
    product_id: '',
    type: getTypeByZone(type),
    monitor_type: type,
    issue_key: issueKey,
    work_space_id: '',
    field_values: [],
  };
  const [loading, setLoading] = useState(false);
  const [statusVisible, setStatusVisible] = useState(false);
  const [defectLists, setDefectLists] = useState<FilterOptions[]>([]);
  const [statusParams, setStatusParams] = useState(initStatusParams);
  const initTitlePrefix = t("【Bugly-性能监控】");
  const [defectTitle, setDefectTitle] = useState('');
  const [fields, setFields] = useState<FieldsType[]>();
  const [innerContent, setInnerContent] = useState(content);
  const [defectList, setDefectList] = useState<IWebhookProjectInfo[]>([]);
  useEffect(() => {
    if (originalDefectList.length === 0) return;
    getFullDefectList({
      userName: () => decodeURIComponent(getUserName()),
      version: () => defaultAppVersion,
    }).then(setDefectList);
  }, [originalDefectList, defaultAppVersion]);
  const handleSelectChange = (val: string | number, key?: string) => {
    setStatusParams({ ...statusParams, [key as string]: val });
    if (onChangeId) onChangeId(val as string);
  };

  const handleSubmit = async () => {
    if (defectTitle.length > 100) {
      message.warning(t("标题长度不能超过100，请修改后再提交"));
      return;
    }
    if (!statusParams.work_space_id) {
      message.warning(t("请选择项目"));
      return;
    }

    // 验证必填字段
    const requiredFields = fields?.filter(field => field.must === 1) || [];
    const emptyRequiredFields = requiredFields.filter(field => field.value === null || field.value === undefined || field.value === '');

    if (emptyRequiredFields.length > 0) {
      const fieldNames = emptyRequiredFields.map(field => field.label).join('、');
      message.error(t('请填写必填字段：{{fieldNames}}', { fieldNames }));
      return;
    }

    setLoading(true);
    const res = await createDefectIssue({
      workSpaceId: statusParams.work_space_id,
      type: statusParams.type,
      productId: statusParams.product_id || getProductId(),
      issueKey: statusParams.issue_key,
      issueType: statusParams.type,
      fieldValues: [
        ...formatFieldsValue(fields ?? []),
        {
          name: 'title',
          label: t("标题"),
          value: `${defectTitle}`,
        },
        {
          name: 'description',
          label: t("详细描述"),
          value: `${innerContent}<a href='${issueUrl}>&gt;&gt;&gt;${t('点击查看详情')}&lt;&lt;&lt;</a>`.replace(/\n/g, '<br>'),
        },
        {
          name: 'buglyIssueUrl',
          label: t("Bugly问题链接"),
          value: issueUrl,
        },
      ],
    });
    if (res) {
      setStatusVisible(false);
      onClose();
      if (onSubmit) {
        onSubmit();
      }
    }
    setLoading(false);
    reportClick(pageId, CUSTOM_ACTIONS.commonCreateTapdBug, { from: pageId });
  };


  const loadDefectFields = async () => {
    setLoading(true);
    const currentDefect = defectList.find(item => item.projectId === statusParams.work_space_id);

    if (currentDefect?.templateTitlePrefix) {
      setDefectTitle(`${currentDefect.templateTitlePrefix}${title || ''}`);
    } else {
      setDefectTitle(`${initTitlePrefix}${title || ''}`);
    }
    if (currentDefect?.data && currentDefect.data.length > 0) {
      const newFields = currentDefect?.data?.map((item: any) => {
        const preset = FIELDS_PRESET.find(p => p.fieldName === item.name);
        if (preset?.defaultComponentType?.type === DefaultComponentType.SELECT) {
          const selectField: FieldsType = {
            name: item.name,
            value: item.value,
            label: item.alias,
            must: item.must,
            type: 'select',
            options: preset.defaultComponentType.options,
          };
          return selectField;
        }
        const defaultField: FieldsType = {
          name: item.name,
          value: item.value,
          label: item.alias,
          must: item.must,
          type: 'default',
        };
        return defaultField;
      }) || [];
      setFields(newFields);
    }

    setLoading(false);
  };

  const getNewFields = (changeValue: string | number, name?: string | null) => {
    const newFields = fields?.map((item: FieldsType) => {
      if (item.name === name) {
        return { ...item, value: changeValue };
      }
      return item;
    });
    setFields(newFields);
  };


  const renderItem = (field: FieldsType) => {
    switch (field.type) {
      case 'select':
        return <RenderSelect
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
          labelWidth={'70px'}
          renderSelectWidth={'314px'}
          style={{ width: '100%' }}
        />;
      case 'autoComplete':
        return <RenderAutoComplete
          name={field.name}
          label={field.label}
          options={field.options!}
          getNewFields={getNewFields}
          value={field.value}
          handleSelectChange={handleSelectChange}
          labelWidth={'70px'}
        />;
      case 'userSelect':
        return <RenderUserSelect
          name={field.name}
          label={field.label}
          getNewFields={getNewFields}
          value={field.value as string}
          labelWidth={'100px'}
        />;
      default:
        return <RenderInput
          name={field.name}
          label={field.label}
          value={field.value}
          must={field.must}
          getNewFields={getNewFields}
          labelWidth={'70px'}
          inputWidth={'250px'}
          style={{ width: '314px' }}
        />;
    }
  };

  useEffect(() => {
    setStatusVisible(visible);
    if (visible) {
      setStatusParams({ ...statusParams, work_space_id: defectList[0]?.projectId as string });
    }
  }, [visible]);


  useEffect(() => {
    if (defectList.length === 0) return;
    const list: FilterOptions[] = [];
    defectList.forEach((item) => {
      list.push({
        label: item.projectName ?? '',
        value: item.projectId ?? '',
      });
    });
    setDefectLists(list);
  }, [defectList]);

  useEffect(() => {
    loadDefectFields();
  }, [statusParams.work_space_id, statusVisible, title]);

  useEffect(() => {
    setInnerContent(content);
  }, [content]);


  return (<>
    <Modal
      visible={statusVisible}
      title={<div style={{
        fontFamily: 'PingFang SC',
        fontWeight: 500,
        fontSize: '16px',
      }}>{t("创建缺陷")}</div>}
      centered
      footer={null}
      onCancel={() => {
        setStatusVisible(false);
        onClose();
      }}
      style={{ minWidth: '676px' }}
    >
      <Spin spinning={loading}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <RenderSelect
            name={'work_space_id'}
            label={t(isProOrSgp() ? '关联项目:' : '关联TAPD')}
            options={defectLists}
            getNewFields={getNewFields}
            value={statusParams.work_space_id ?? defectLists[0]?.value}
            handleSelectChange={handleSelectChange}
            labelWidth={'63px'}
            renderSelectWidth={'100%'}
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ display: 'flex' }}>
          <span style={{ flexShrink: 0, lineHeight: '30px', marginRight: '12px', width: '63px', textAlign: 'right' }}>{t("缺陷标题:")}</span>
          <Input
            value={`${defectTitle}`}
            onChange={e => setDefectTitle(e.target.value)}
          />
        </div>
        <div style={{ marginBottom: '20px', marginTop: '30px', fontSize: '16px', fontWeight: 500, fontFamily: 'PingFang SC' }}>
          {t("基本信息")}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
          {fields?.length ? fields.map(field => renderItem(field)) : null}
        </div>
        <div>
          <div style={{ marginBottom: '20px', marginTop: '20px', fontSize: '14px', fontWeight: 500, fontFamily: 'PingFang SC' }}>{t("缺陷内容")}</div>
          <Input.TextArea
            value={innerContent}
            style={{ minHeight: '200px' }}
            onChange={(e) => {
              setInnerContent(e.target.value);
            }}
          />
        </div>
        <div style={{ textAlign: 'right', marginTop: '40px' }}>
          <Button onClick={() => {
            setStatusVisible(false);
            onClose();
          }}>
            {t("取消")}
          </Button>
          <Button type='primary' onClick={handleSubmit} style={{ marginLeft: '20px' }}>
            {t("确定")}
          </Button>
        </div>
      </Spin>
    </Modal>
  </>
  );
});
